import React from "react";
import { FaSave, FaPlus, FaTrash, FaEdit } from "react-icons/fa";
import InlineAlert from "../../../UI/DesignStandards/Notifications/InlineAlert";
import {
  formatPhoneNumberAsTyped,
  formatPhoneNumber,
} from "../../../../utility/forms/formInputFormat";
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  SoftButton,
} from "../../../UI/DesignStandards/Buttons";

const OrganizationContactsSection = ({
  contacts,
  formError,
  showAddContact,
  editingContact,
  newContact,
  onSetShowAddContact,
  onSetEditingContact,
  onContactChange,
  onSaveContact,
  deletingContactId,
  deletingContactInProgressId,
  onStartConfirmDelete,
  onCancelConfirmDelete,
  onConfirmDelete,
}) => {
  return (
    <div className="card-board p-4 sm:p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
        <h2 className="heading-display text-xl">Organization Contacts</h2>
        <SecondaryButton
          onClick={() => onSetShowAddContact(true)}
          className="!px-3 !py-2 text-sm"
        >
          <FaPlus className="w-4 h-4" />
          <span>Add Contact</span>
        </SecondaryButton>
      </div>

      {showAddContact && !editingContact && (
        <div className="mb-6 p-4 card-inset">
          <h3 className="text-lg font-medium mb-4">Add New Contact</h3>
          {formError ? (
            <div className="mb-3">
              <InlineAlert
                type="error"
                title="Please fix the following"
                message={formError}
              />
            </div>
          ) : null}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSaveContact();
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={newContact.name || ""}
                  onChange={(e) => onContactChange("name", e.target.value)}
                  placeholder="Contact name"
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={newContact.email || ""}
                  onChange={(e) => onContactChange("email", e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={newContact.phone || ""}
                  onChange={(e) => onContactChange("phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Role
                </label>
                <input
                  type="text"
                  value={newContact.organizationRole || ""}
                  onChange={(e) =>
                    onContactChange("organizationRole", e.target.value)
                  }
                  placeholder="e.g., President, Secretary"
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <PrimaryButton type="submit">
                <FaSave className="w-4 h-4" />
                <span>Add Contact</span>
              </PrimaryButton>
              <button
                type="button"
                onClick={() => {
                  onSetShowAddContact(false);
                  onContactChange("resetNew", null);
                }}
                className="inline-flex items-center min-h-11 px-3 py-2 text-sm text-brand-charcoal bg-brand-cream font-display font-bold tracking-wide border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {contacts.length === 0 ? (
        <div className="text-center py-8 text-brand-muted">
          <svg
            className="text-4xl mx-auto mb-4 text-brand-mist"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p>No contacts added yet.</p>
          <p className="text-sm mt-2">
            Click "Add Contact" to add organization contacts.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              className="flex flex-col border border-brand-charcoal/10 rounded-lg hover:bg-brand-cream/50 transition-colors p-4 mb-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-4">
                    {contact.name && (
                      <span className="font-medium text-brand-charcoal">
                        {contact.name}
                      </span>
                    )}
                    {contact.email && (
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 text-brand-muted mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-brand-muted text-sm sm:text-base">
                          {contact.email}
                        </span>
                      </div>
                    )}
                    {contact.phone && (
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 text-brand-muted mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span className="text-brand-muted text-sm sm:text-base">
                          {formatPhoneNumberAsTyped(contact.phone)}
                        </span>
                      </div>
                    )}
                    {contact.organizationRole && (
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 text-brand-muted mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <span className="text-brand-muted text-sm sm:text-base">
                          {contact.organizationRole}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <SoftButton
                    color="cream"
                    size="sm"
                    onClick={() => onSetEditingContact(contact)}
                    title={`Edit ${contact.name}`}
                    iconLeft={<FaEdit className="h-3.5 w-3.5" />}
                  >
                    Edit
                  </SoftButton>
                  {deletingContactId === contact.id ? (
                    <div
                      className="flex items-center gap-2"
                      role="group"
                      aria-label="Delete confirmation"
                    >
                      <span className="text-sm text-red-600 font-medium">
                        Delete?
                      </span>
                      <SoftButton
                        color="red"
                        size="xs"
                        type="button"
                        onClick={() => onConfirmDelete(contact.id)}
                        disabled={deletingContactInProgressId === contact.id}
                        aria-label={`Confirm delete ${contact.name}`}
                      >
                        {deletingContactInProgressId === contact.id
                          ? "Deleting..."
                          : "Yes"}
                      </SoftButton>
                      <SoftButton
                        color="cream"
                        size="xs"
                        type="button"
                        onClick={() => onCancelConfirmDelete()}
                        aria-label={`Cancel delete ${contact.name}`}
                      >
                        No
                      </SoftButton>
                    </div>
                  ) : (
                    <SoftButton
                      color="red"
                      size="sm"
                      onClick={() => onStartConfirmDelete(contact.id)}
                      title={`Delete ${contact.name}`}
                      aria-label={`Delete ${contact.name}`}
                      iconLeft={<FaTrash className="h-3.5 w-3.5" />}
                    >
                      Delete
                    </SoftButton>
                  )}
                </div>
              </div>
              {editingContact && editingContact.id === contact.id && (
                <div className="mt-4 p-4 card-inset">
                  <h3 className="text-lg font-medium mb-4">Edit Contact</h3>
                  {formError ? (
                    <div className="mb-3">
                      <InlineAlert
                        type="error"
                        title="Please fix the following"
                        message={formError}
                      />
                    </div>
                  ) : null}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSaveContact();
                    }}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-charcoal mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={editingContact.name || ""}
                          onChange={(e) =>
                            onContactChange("name", e.target.value)
                          }
                          placeholder="Contact name"
                          className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-charcoal mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={editingContact.email || ""}
                          onChange={(e) =>
                            onContactChange("email", e.target.value)
                          }
                          placeholder="email@example.com"
                          className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-charcoal mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={formatPhoneNumberAsTyped(
                            editingContact.phone || ""
                          )}
                          onChange={(e) =>
                            onContactChange("phone", e.target.value)
                          }
                          placeholder="(555) 123-4567"
                          className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-charcoal mb-2">
                          Role
                        </label>
                        <input
                          type="text"
                          value={editingContact.organizationRole || ""}
                          onChange={(e) =>
                            onContactChange("organizationRole", e.target.value)
                          }
                          placeholder="e.g., President, Secretary"
                          className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                      <button
                        type="submit"
                        className="inline-flex items-center min-h-11 bg-brand-gold text-brand-charcoal px-3 py-2 rounded-lg text-sm font-display font-bold tracking-wide hover:bg-brand-gold/90"
                      >
                        <FaSave className="mr-2 h-4 w-4" /> Update Contact
                      </button>
                      <button
                        type="button"
                        onClick={() => onSetEditingContact(null)}
                        className="inline-flex items-center min-h-11 px-3 py-2 text-sm text-brand-charcoal bg-brand-cream font-display font-bold tracking-wide border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrganizationContactsSection;
